import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, Wrench, Siren, ArrowUpRight, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { resolveMedia } from "../../lib/media";
import AdminUrgentRescueForm, { BLANK_RESCUE } from "./AdminUrgentRescueForm";
import AdminUrgentRescueTools from "./AdminUrgentRescueTools";
import AdminAnnouncementEditor from "./AdminAnnouncementEditor";

const iconBtn = "text-xs px-2 py-1 border border-zinc-900 inline-flex items-center gap-1 hover:bg-zinc-900 hover:text-white";

function Row({ r, headers, reload, onEdit }) {
  const [open, setOpen] = useState(false);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch { toast.error("Action failed."); } };
  const publish = () => {
    if (!r.is_published && !window.confirm(`Publish "${r.title}" publicly now?`)) return;
    act(() => api.post(`/admin/urgent-rescues/${r.id}/${r.is_published ? "unpublish" : "publish"}`, {}, { headers }), r.is_published ? "Moved back to draft." : "Published.");
  };
  const toggleStrip = () => act(() => api.put(`/admin/urgent-rescues/${r.id}`, { is_strip_active: !r.is_strip_active }, { headers }), r.is_strip_active ? "Removed from strip." : "Added to strip.");
  const del = () => window.confirm(`Delete "${r.title}"? This cannot be undone.`) && act(() => api.delete(`/admin/urgent-rescues/${r.id}`, { headers }), "Deleted.");
  return (
    <li className="border border-zinc-300 bg-white" data-testid={`admin-urgent-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        {r.photo_url ? <img src={resolveMedia(r.photo_url)} alt="" className="w-14 h-14 object-cover border" /> : <div className="w-14 h-14 bg-zinc-100 flex items-center justify-center"><Siren size={16} /></div>}
        <div className="flex-1 min-w-[200px]">
          <div className="flex flex-wrap gap-1 mb-1">
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${r.is_published ? "bg-green-700 text-white" : "bg-zinc-200"}`} data-testid={`admin-urgent-pub-${r.id}`}>{r.is_published ? "Published" : "Draft"}</span>
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-600 text-white">{r.status}</span>
            {r.verification?.startsWith("Reported") && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-200">Unconfirmed</span>}
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 border">{r.help_offer_count || 0} offers</span>
          </div>
          <div className="font-bold text-sm">{r.title}</div>
          <div className="text-[11px] text-zinc-500 font-mono">{r.city}, {r.state_code || r.state}{r.incident_date && ` · ${r.incident_date}`}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          <button onClick={toggleStrip} className={`${iconBtn} ${r.is_strip_active ? "!bg-red-600 !text-white !border-red-600" : ""}`} data-testid={`admin-urgent-strip-${r.id}`}><Siren size={11} /> Strip {r.is_strip_active ? "on" : "off"}</button>
          <button onClick={() => onEdit(r)} className={iconBtn} data-testid={`admin-urgent-edit-${r.id}`}><Pencil size={11} /> Edit</button>
          <button onClick={() => setOpen((v) => !v)} className={iconBtn} data-testid={`admin-urgent-tools-${r.id}`}><Wrench size={11} /> Tools</button>
          <button onClick={publish} className={iconBtn} data-testid={`admin-urgent-publish-${r.id}`}>{r.is_published ? <><EyeOff size={11} /> Unpublish</> : <><Eye size={11} /> Publish</>}</button>
          {!r.is_published && <Link to={`/urgent-rescues/${r.id}?preview=1`} className={iconBtn} data-testid={`admin-urgent-preview-${r.id}`}><Eye size={11} /> Preview</Link>}
          {!r.is_published && <Link to={`/?campaign_preview=${r.id}`} className={iconBtn} data-testid={`admin-urgent-home-preview-${r.id}`}><Eye size={11} /> Preview on homepage</Link>}
          {r.is_published && <a href={`/urgent-rescues/${r.id}`} target="_blank" rel="noreferrer" className={iconBtn} data-testid={`admin-urgent-view-${r.id}`}><ArrowUpRight size={11} /> View</a>}
          <button onClick={del} className={`${iconBtn} text-red-600`} data-testid={`admin-urgent-delete-${r.id}`}><Trash2 size={11} /></button>
        </div>
      </div>
      {open && <AdminUrgentRescueTools rescue={r} headers={headers} onChanged={reload} />}
    </li>
  );
}

export default function AdminUrgentRescuesList({ token }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try { const { data } = await api.get("/admin/urgent-rescues", { headers }); setItems(data.items || []); }
    catch { toast.error("Failed to load urgent rescues."); }
    finally { setLoading(false); }
  }, [token, headers]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-4" data-testid="admin-urgent-rescues">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-2xl inline-flex items-center gap-2"><Siren className="text-red-600" /> Urgent Rescue Alerts</h2>
        <div className="flex gap-2">
          <button onClick={load} className={iconBtn} data-testid="admin-urgent-refresh">{loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh</button>
          <button onClick={() => setEditing({ ...BLANK_RESCUE })} className="btn-danger" data-testid="admin-urgent-new"><Plus size={14} /> New alert</button>
        </div>
      </div>
      <p className="text-xs text-zinc-600">Every alert starts as a draft. Nothing is public until you click Publish.</p>
      <AdminAnnouncementEditor headers={headers} />
      {editing && <AdminUrgentRescueForm key={editing.id || "new"} initial={editing} headers={headers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ul className="space-y-2">{items.map((r) => <Row key={r.id} r={r} headers={headers} reload={load} onEdit={setEditing} />)}</ul>
      {!loading && !items.length && <p className="text-sm text-zinc-500">No urgent rescue alerts yet.</p>}
    </section>
  );
}
